import { Grammerly } from 'iconsax-react';
import { noop } from 'lodash-es';
import mergeRefs from 'merge-refs';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useController } from 'react-hook-form';
import { useEventListener } from 'usehooks-ts';
import KeyboardIcon from '@/assets/images/icons/keyboard.svg?react';
import useConversationForm from '@/hooks/conversations/use-conversation-form';
import cn from '@/utils/merge-class-names';
import QuillEditor, { type EditorActions } from './quill-editor';

interface FormTextAreaProps {
  disabled?: boolean;
  initialValue?: string;
  isLoading?: boolean;
  isSymbolMenuOpen?: boolean;
  onChange?: (value: string) => void;
  onToggleSymbolMenu?: (open?: boolean) => void;
}

const ConversationTextArea = forwardRef<EditorActions, FormTextAreaProps>(
  ({ initialValue, disabled, isSymbolMenuOpen, onToggleSymbolMenu }, ref) => {
    const {
      form: { control },
    } = useConversationForm();
    const {
      field,
      fieldState: { error },
    } = useController({
      control,
      name: 'Content',
      defaultValue: '',
    });
    const editorRef = useRef<EditorActions>(null);

    useImperativeHandle(
      ref,
      (): EditorActions => ({
        focus: noop,
        blur: noop,
        insertText: noop,
        insertMention: noop,
        reset: noop,
        closeMentionMenu: noop,
        ...(editorRef.current || {}),
      }),
    );

    useEventListener(
      'visibilitychange',
      () => {
        editorRef.current?.blur();
      },
      useRef(document),
    );

    return (
      <div
        className={cn(
          'flex min-h-10 w-full flex-1 items-center transition-colors desktop:gap-4 textarea desktop:bg-transparent ring-primary border-none overflow-hidden p-0',
          error && 'textarea-error',
        )}>
        <QuillEditor
          ref={mergeRefs(editorRef, field.ref)}
          initialValue={initialValue}
          onFocus={() => onToggleSymbolMenu?.(false)}
        />

        <button
          type='button'
          disabled={disabled}
          onMouseDown={(e) => {
            e.preventDefault();
            onToggleSymbolMenu?.();
          }}
          className='btn btn-square btn-ghost btn-sm size-8'>
          {isSymbolMenuOpen ? (
            <KeyboardIcon className='size-6 text-body' />
          ) : (
            <Grammerly className='size-6 text-body' />
          )}
        </button>

        <div className='hidden h-8 w-px bg-base-200 desktop:block self-end' />
      </div>
    );
  },
);

ConversationTextArea.displayName = 'ConversationTextArea';

export default ConversationTextArea;
